import { ComponentType } from '@angular/cdk/overlay';
import { InjectionToken } from '@angular/core';
import { SnackbarModel } from '@libs/common/models';

export const FLASH_MESSAGE_COMPONENT_TYPE: InjectionToken<ComponentType<unknown>>
  = new InjectionToken<ComponentType<unknown>>('Flash message component constructor');

// eslint-disable-next-line @typescript-eslint/typedef
export const FlashMessageNames = [
  // Authentication
  'successLogin',
  'errorLogin',
  'errorRegistration',

  // User
  'successUserDataUpdate',
  'errorUserDataUpdate',
  'successUserEmailUpdate',
  'errorUserEmailUpdate',

  // Customer
  'successCustomerAliasUpdate',
  'errorCustomerAliasUpdate',
  'successCustomerSwitch',
  'errorCustomerSwitch',

  // Cart
  'successAddToCart',
  'errorAddToCart',
  'successChangeQuantity',
  'errorChangeQuantity',
  'successRemoveFromCart',
  'errorRemoveFromCart',
  'successDeleteAllItemsCart',
  'errorDeleteAllItemsCart',

  // Checkout
  'warningCheckoutValidation',
  'errorCheckout',
  'errorCheckoutNoAddresses',
  'errorCheckoutCartValidationFailed',
  'errorCheckoutOrderAlreadyPlacedForThisCart',
  'infoCheckoutCartChanged',

  // Shopping lists
  'successAddToShoppingList',
  'errorAddToShoppingList',
  'successRemoveFromShoppingList',
  'errorRemoveFromShoppingList',
  'errorLoadList',
  'successCreateShoppingList',
  'errorCreateShoppingList',
  'successUpdateShoppingList',
  'errorUpdateShoppingList',
  'successRemoveShoppingList',
  'errorRemoveShoppingList',
  'errorListDownload',

  // Search
  'emptySearchResult',
  'errorSearchResult',

  // Scanner
  'errorScannerProductNotFound',

  // Voucher
  'successAddVoucher',
  'successRemoveVoucher',
  'errorRemoveVoucher',
  'errorGenericAddVoucher',

  // SRM Service
  'errorSrmLoadForm',
  'errorSrmCreateTicket',
  'errorSrmNavigation',

  // Documents Service
  'errorDocumentDownload',

  // Photo Picker
  'errorPhotoPicker',

  // Content
  'successContentSubmitForm',
  'errorContentSubmitForm',
] as const;

export type FlashMessageType = typeof FlashMessageNames[number];

export const FlashMessages: Record<FlashMessageType, SnackbarModel> = {
  // Authentication
  successLogin: {
    theme: 'success',
    message: 'Login erfolgreich.',
  },
  errorLogin: {
    theme: 'error',
    message: 'Login fehlgeschlagen.',
  },
  errorRegistration: {
    theme: 'error',
    message: 'Die Registrierungsseite konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
  },

  // User
  successUserDataUpdate: {
    theme: 'success',
    message: 'Ihre Änderungen wurden gespeichert.',
  },
  errorUserDataUpdate: {
    theme: 'error',
    message: 'Ihre Änderungen konnten leider nicht gespeichert werden. Bitte versuchen Sie es erneut.',
  },
  successUserEmailUpdate: {
    theme: 'success',
    message: 'Vielen Dank, Ihre Änderungsanfrage wurde versendet.',
  },
  errorUserEmailUpdate: {
    theme: 'error',
    message: 'Ihre Anfrage konnte leider nicht versendet werden. Bitte versuche Sie es erneut.',
  },

  // Customer
  successCustomerAliasUpdate: {
    theme: 'success',
    message: 'Bezeichnung für die Kundennummer wurde gespeichert.',
  },
  errorCustomerAliasUpdate: {
    theme: 'error',
    message: 'Es ist ein Fehler aufgetreten.',
  },
  successCustomerSwitch: {
    theme: 'success',
    message: 'Wechseln der Geschäftseinheit erfolgreich.',
  },
  errorCustomerSwitch: {
    theme: 'error',
    message: 'Wechseln der Geschäftseinheit fehlgeschlagen.',
  },

  // Cart
  successAddToCart: {
    theme: 'success',
    message: 'Artikel erfolgreich zum Warenkorb hinzugefügt.',
  },
  errorAddToCart: {
    theme: 'error',
    message: 'Das Produkt konnte leider nicht in den Warenkorb gelegt werden.',
  },
  successChangeQuantity: {
    theme: 'success',
    message: 'Menge erfolgreich geändert.',
  },
  errorChangeQuantity: {
    theme: 'error',
    message: 'Die Menge konnte leider nicht geändert werden.',
  },
  successRemoveFromCart: {
    theme: 'success',
    message: 'Artikel erfolgreich aus dem Warenkorb entfernt.',
  },
  errorRemoveFromCart: {
    theme: 'error',
    message: 'Das Produkt konnte leider nicht aus dem Warenkorb entfernt werden.',
  },
  successDeleteAllItemsCart: {
    theme: 'success',
    message: 'Alle Artikel erfolgreich aus dem Warenkorb entfernt.',
  },
  errorDeleteAllItemsCart: {
    theme: 'error',
    message: 'Beim Entfernen aller Artikel aus dem Warenkorb ist ein Fehler aufgetreten.',
  },

  // Checkout
  warningCheckoutValidation: {
    theme: 'warning',
    message: 'Bei der Überprüfung Ihrer Bestellung sind Fehler aufgetreten. Bitte beachten Sie die Hinweise am Anfang der Seite.',
    duration: 5000,
  },
  errorCheckout: {
    theme: 'error',
    // eslint-disable-next-line max-len
    message: 'Die Bestellung konnte leider nicht abgeschickt werden. Bitte versuche Sie es in ein paar Minuten erneut. Wenn das Problem besteht bitte kontaktieren Sie den Support.',
    duration: 10000,
  },
  errorCheckoutNoAddresses: {
    theme: 'error',
    message: 'In Ihrem Account fehlt entweder die Rechnungsaddresse oder Lieferaddresse bitte kontaktieren Sie den Support.',
    duration: 10000,
  },
  errorCheckoutCartValidationFailed: {
    theme: 'error',
    message: 'Beim Überprüfen des Warenkorbs sind Fehler aufgetreten. Bitte prüfen Sie Ihre Bestellung.',
    duration: 10000,
  },
  errorCheckoutOrderAlreadyPlacedForThisCart: {
    theme: 'error',
    message: 'Es wurde bereits eine Bestellung für diesen Warenkorb aufgegeben.',
    duration: 10000,
  },
  infoCheckoutCartChanged: {
    theme: 'info',
    // eslint-disable-next-line max-len
    message: 'Der Warenkorb wurde in der Zwischenzeit verändert und muss erneut überprüft werden. Wenn die Überprüfung fertig ist versuchen Sie bitte erneut die Bestellung aufzugeben.',
    duration: 10000,
  },

  // Shopping lists
  successAddToShoppingList: {
    theme: 'success',
    message: 'Artikel hinzugefügt',
    useIcon: true,
  },
  errorAddToShoppingList: {
    theme: 'error',
    message: 'Artikel konnte nicht hinzugefügt werden.',
  },
  successRemoveFromShoppingList: {
    theme: 'success',
    message: 'Artikel wurde gelöscht.',
  },
  errorRemoveFromShoppingList: {
    theme: 'error',
    message: 'Artikel konnte nicht gelöscht werden.',
  },
  errorLoadList: {
    theme: 'error',
    message: 'Die Liste konnte nicht geladen werden, bitte versuchen Sie es später noch einmal.',
  },
  successCreateShoppingList: {
    theme: 'success',
    message: 'Einkaufsliste erfolgreich erstellt.',
  },
  errorCreateShoppingList: {
    theme: 'error',
    message: 'Einkaufsliste konnte nicht erstellt werden.',
  },
  successUpdateShoppingList: {
    theme: 'success',
    message: 'Einkaufsliste erfolgreich gespeichert.',
  },
  errorUpdateShoppingList: {
    theme: 'error',
    message: 'Einkaufsliste konnte nicht gespeichert werden.',
  },
  successRemoveShoppingList: {
    theme: 'success',
    message: 'Einkaufsliste erfolgreich gelöscht.',
  },
  errorRemoveShoppingList: {
    theme: 'error',
    message: 'Einkaufsliste konnte nicht gelöscht werden.',
  },
  errorListDownload: {
    theme: 'error',
    message: 'Der Listendownload ist fehlgeschlagen.'
  },

  // Search
  emptySearchResult: {
    theme: 'info',
    message: 'Leider haben wir nichts gefunden.',
  },
  errorSearchResult: {
    theme: 'error',
    message: 'Bei der Suche ist leider ein Fehler aufgetreten.',
  },

  // scanner
  errorScannerProductNotFound: {
    theme: 'error',
    message: 'Artikel nicht gefunden',
  },

  // Voucher
  successAddVoucher: {
    theme: 'info',
    message: 'Der Gutschein wurde erfolgreich hinzugefügt.',
  },
  successRemoveVoucher: {
    theme: 'info',
    message: 'Der Gutschein wurde erfolgreich entfernt.',
  },
  errorRemoveVoucher: {
    theme: 'error',
    message: 'Bei dem Entfernen des Gutscheins ist leider ein Fehler aufgetreten.',
  },
  errorGenericAddVoucher: {
    theme: 'error',
    message: 'Der Gutschein konnte nicht angewandt werden.',
  },

  // SRM
  errorSrmLoadForm: {
    theme: 'error',
    message: 'Das Formular konnte nicht geladen werden. Bitte versuchen Sie es später erneut.',
    duration: 10000,
  },
  errorSrmCreateTicket: {
    theme: 'error',
    message: 'Ihr Anfrage konnte nicht erstellt werden. Bitte versuchen Sie es später erneut.',
    duration: 10000,
  },
  errorSrmNavigation: {
    theme: 'error',
    message: 'Es ist leider ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
    duration: 10000,
  },

  // Documents
  errorDocumentDownload: {
    theme: 'error',
    message: 'Das Dokument konnte nicht heruntergeladen werden. Bitte versuchen Sie es später noch einmal.',
    duration: 10000,
  },

  // Photo Picker
  errorPhotoPicker: {
    theme: 'error',
    // eslint-disable-next-line max-len
    message: 'Das Foto konnte nicht hinzugefügt werden. Die App benötigt die Erlaubnis, die Kamera oder Fotos zu verwenden. Diese Erlaubnis kann in den Einstellungen erteilt werden.',
    duration: 10000,
  },

  // Content
  successContentSubmitForm: {
    theme: 'success',
    message: 'Das Formular wurde übermittelt.',
    duration: 5000,
  },
  errorContentSubmitForm: {
    theme: 'error',
    message: 'Es ist leider ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
    duration: 5000,
  },
};
